/* ===========================================================================
   ui-delta — useTheme
   ---------------------------------------------------------------------------
   ADDS:      Theme preference: read, set, and follow the OS. Persists the
              user's explicit choice; absence of a choice means "follow the OS".
   UPSTREAM:  No equivalent in @ui.
   AT MERGE:  Move into @ui alongside theme.css. The storage key `elfie-theme`
              is deliberately product-neutral so the value carries across.
   ORIGIN:    elfie-design guide, tokens/theme.js and components/layouts/
              ThemeToggle.d.ts — ported from framework-free JS to a Vue
              composable per STACK.md.
   =========================================================================== */
import { onScopeDispose, readonly, ref } from 'vue'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'elfie-theme'

const readStored = (): ResolvedTheme | null => {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === 'dark' || value === 'light' ? value : null
  } catch {
    // Private browsing and blocked site data both throw. Fall back to the OS.
    return null
  }
}

const systemTheme = (): ResolvedTheme =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

/*
 * Module-level so every caller shares one source of truth — two components
 * reading the theme must never disagree about what is on screen.
 */
const preference = ref<ThemePreference>(readStored() ?? 'system')
const resolved = ref<ResolvedTheme>(readStored() ?? systemTheme())

const apply = (next: ThemePreference): void => {
  const root = document.documentElement
  if (next === 'system') {
    delete root.dataset.theme
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Nothing to do — the in-memory refs still reflect the choice.
    }
  } else {
    root.dataset.theme = next
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // As above: the choice holds for this session even if it cannot persist.
    }
  }
  preference.value = next
  resolved.value = next === 'system' ? systemTheme() : next
}

/** Call once, as early as possible, to reconcile the refs with the DOM. */
export const initTheme = (): void => {
  const stored = readStored()
  if (stored) document.documentElement.dataset.theme = stored
  preference.value = stored ?? 'system'
  resolved.value = stored ?? systemTheme()
}

export const useTheme = () => {
  // While the user is following the OS, an OS-level change must move the page.
  const media = window.matchMedia?.('(prefers-color-scheme: dark)')
  const onSystemChange = (): void => {
    if (preference.value === 'system') resolved.value = systemTheme()
  }
  media?.addEventListener('change', onSystemChange)
  onScopeDispose(() => media?.removeEventListener('change', onSystemChange))

  return {
    preference: readonly(preference),
    resolved: readonly(resolved),
    setTheme: apply,
    toggle: () => apply(resolved.value === 'dark' ? 'light' : 'dark'),
  }
}
